/**
 * Wave 3 P2d (2026-05-22): pure-helper module for BottomNav role-aware
 * item selection. Extracted from BottomNav.tsx so vitest can test the
 * logic without dragging in React + zustand + window.matchMedia (which
 * jsdom doesn't have).
 *
 * Caller: `src/components/layout/BottomNav.tsx`
 * Tests:  `src/test/bottom-nav-role-filter.test.ts`
 */

import { Home, Camera, Clock, Car, MoreHorizontal, ClipboardCheck, Users, User } from 'lucide-react';
import type { UserRole } from '@/types';

export type NavItem = {
  to: string;
  icon: typeof Home;
  label: string;
  minRole?: UserRole;
};

// P3 (2026-06-16): field-optimized for phones. An owner-operator on a phone
// snaps receipts (Smart Capture), logs locums days/hours (Time), and logs trips
// (Mileage) — so those are the quick tabs. Expenses + Reports (more desk-bound)
// move into the "More" drawer, which is the full sidebar one tap away.
export const OWNER_ITEMS: NavItem[] = [
  { to: '/',          icon: Home,             label: 'Home' },
  { to: '/capture',   icon: Camera,           label: 'Smart Capture',  minRole: 'bookkeeper' },
  { to: '/hours',     icon: Clock,            label: 'Time' },
  { to: '/mileage',   icon: Car,              label: 'Mileage' },
  { to: '/more',      icon: MoreHorizontal,   label: 'More' },
];

export const MANAGER_ITEMS: NavItem[] = [
  { to: '/manager/approvals', icon: ClipboardCheck, label: 'Approvals' },
  { to: '/manager/employees', icon: Users,          label: 'Team' },
  { to: '/employee/profile',  icon: User,           label: 'Profile' },
];

/**
 * Pure helper for the role-aware item selection. Pure JS — no React,
 * no zustand, no DOM. Safe to call from vitest with synthetic role +
 * hasMinRole closure.
 *
 * - Pure manager (role==='manager') → MANAGER_ITEMS (manager has access
 *   to all 3 by construction; no further filtering needed).
 * - All other roles → OWNER_ITEMS filtered by hasMinRole on the item's
 *   minRole (Capture requires bookkeeper, Reports requires accountant,
 *   others have no minRole and pass through).
 */
export function selectBottomNavItems(
  role: UserRole,
  hasMinRole: (r: UserRole) => boolean,
): NavItem[] {
  if (role === 'manager') return MANAGER_ITEMS;
  return OWNER_ITEMS.filter((item) => !item.minRole || hasMinRole(item.minRole));
}
